import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ScrollProvider } from "@/contexts/ScrollContext";

// Google Analytics page tracking for SPA
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    window.gtag?.('event', 'page_view', {
      page_path: location.pathname + location.search,
    });
  }, [location]);

  return null;
}

// Lazy load all page components for better performance
const Index = lazyWithRetry(() => import("./pages/Index"));
const ToddlerProgram = lazyWithRetry(() => import("./pages/ToddlerProgram"));
const ToddlerProgramCopy = lazyWithRetry(() => import("./pages/ToddlerProgramCopy"));
const PrimaryProgram = lazyWithRetry(() => import("./pages/PrimaryProgram"));
const KindergartenProgram = lazyWithRetry(() => import("./pages/KindergartenProgram"));
const ElementaryProgram = lazyWithRetry(() => import("./pages/ElementaryProgram"));
const MiddleSchoolProgram = lazyWithRetry(() => import("./pages/MiddleSchoolProgram"));

const SummerPrograms = lazyWithRetry(() => import("./pages/SummerPrograms"));

const TuitionSchedule = lazyWithRetry(() => import("./pages/TuitionSchedule"));
const FacultyAndAdministration = lazyWithRetry(() => import("./pages/FacultyAndAdministration"));
const LowerSchoolStaff = lazyWithRetry(() => import("./pages/LowerSchoolStaff"));
const UpperSchoolStaff = lazyWithRetry(() => import("./pages/UpperSchoolStaff"));
const SpecialsStaff = lazyWithRetry(() => import("./pages/SpecialsStaff"));
const AdministrationStaff = lazyWithRetry(() => import("./pages/AdministrationStaff"));

const Graduates = lazyWithRetry(() => import("./pages/Graduates"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const LocationAndFacilities = lazyWithRetry(() => import("./pages/LocationAndFacilities"));
const TransferStudents = lazyWithRetry(() => import("./pages/TransferStudents"));
const Welcome = lazyWithRetry(() => import("./pages/Welcome"));
const About = lazyWithRetry(() => import("./pages/About"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const TourRegistration = lazyWithRetry(() => import("./pages/TourRegistration"));
const ScheduleTour = lazyWithRetry(() => import("./pages/ScheduleTour"));
const ApplyToWFM = lazyWithRetry(() => import("./pages/ApplyToWFM"));
const ApplicationSubmitted = lazyWithRetry(() => import("./pages/ApplicationSubmitted"));

const UpperSchoolAdmissions = lazyWithRetry(() => import("./pages/UpperSchoolAdmissions"));
const OpenHouseEvents = lazyWithRetry(() => import("./pages/OpenHouseEvents"));
const StaffResources = lazyWithRetry(() => import("./pages/StaffResources"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Curriculum = lazyWithRetry(() => import("./pages/Curriculum"));
const History = lazyWithRetry(() => import("./pages/History"));
const MissionAndPhilosophy = lazyWithRetry(() => import("./pages/MissionAndPhilosophy"));
const WhyWaterfront = lazyWithRetry(() => import("./pages/WhyWaterfront"));
const AuthenticMontessori = lazyWithRetry(() => import("./pages/AuthenticMontessori"));
const Inquiry = lazyWithRetry(() => import("./pages/Inquiry"));
const ParentPortal = lazyWithRetry(() => import("./pages/ParentPortal"));
const AfterSchoolActivities = lazyWithRetry(() => import("./pages/AfterSchoolActivities"));
const ThankYou = lazyWithRetry(() => import("./pages/ThankYou"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PageTracker />
        <ScrollProvider>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/infant-program" element={<ToddlerProgram />} />
          <Route path="/toddler-program" element={<ToddlerProgramCopy />} />
          <Route path="/toddler-program-copy" element={<Navigate to="/toddler-program" replace />} />
          <Route path="/primary-program" element={<PrimaryProgram />} />
          <Route path="/kindergarten-program" element={<KindergartenProgram />} />
          <Route path="/elementary-program" element={<ElementaryProgram />} />
          <Route path="/middle-school-program" element={<MiddleSchoolProgram />} />
          
          <Route path="/summer-programs" element={<SummerPrograms />} />
          
          <Route path="/tuition-schedule" element={<TuitionSchedule />} />
          <Route path="/faculty-and-administration" element={<FacultyAndAdministration />} />
          <Route path="/faculty-and-administration/lower-school" element={<LowerSchoolStaff />} />
          <Route path="/faculty-and-administration/upper-school" element={<UpperSchoolStaff />} />
          <Route path="/faculty-and-administration/specials" element={<SpecialsStaff />} />
          <Route path="/faculty-and-administration/administration" element={<AdministrationStaff />} />
          <Route path="/faculty-and-administration/leadership" element={<Navigate to="/welcome" replace />} />
          <Route path="/graduates" element={<Graduates />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/our-campus" element={<LocationAndFacilities />} />
          <Route path="/transfer-students" element={<TransferStudents />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/open-house-events" element={<OpenHouseEvents />} />
          <Route path="/tour-registration" element={<TourRegistration />} />
          <Route path="/schedule-tour/:tourType" element={<ScheduleTour />} />
          <Route path="/apply-to-wfm" element={<ApplyToWFM />} />
          <Route path="/application-submitted" element={<ApplicationSubmitted />} />
          
          
          <Route path="/application" element={<UpperSchoolAdmissions />} />
          <Route path="/staff-resources" element={<StaffResources />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* New SEO Pages */}
          <Route path="/curriculum" element={<Curriculum />} />
          <Route path="/history" element={<History />} />
          <Route path="/mission-and-philosophy" element={<MissionAndPhilosophy />} />
          <Route path="/why-waterfront" element={<WhyWaterfront />} />
          <Route path="/authentic-montessori-school" element={<AuthenticMontessori />} />
          <Route path="/inquiry" element={<Inquiry />} />
          <Route path="/parent-portal" element={<ParentPortal />} />
          <Route path="/after-school-activities" element={<AfterSchoolActivities />} />
          <Route path="/after-school" element={<Navigate to="/after-school-activities" replace />} />
          <Route path="/calendar" element={<Navigate to="/parent-portal" replace />} />
          <Route path="/calendar/:year" element={<Navigate to="/parent-portal" replace />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/thank-you-open-house" element={<ThankYou />} />
          <Route path="/thank-you-tour-registration" element={<ThankYou />} />
          
          {/* Redirects for old URLs - SEO preservation */}
          <Route path="/location-and-facilities" element={<Navigate to="/our-campus" replace />} />
          <Route path="/news" element={<Navigate to="/blog" replace />} />
          <Route path="/news/2024/10/29/the-rationale-for-practical-life" element={<Navigate to="/blog/the-rationale-for-practical-life" replace />} />
          <Route path="/news/2024/11/25/5-montessori-activities-for-thanksgiving" element={<Navigate to="/blog/5-montessori-activities-for-thanksgiving" replace />} />
          <Route path="/news/2022/10/21/whats-so-extraordinary-about-the-ordinary" element={<Navigate to="/blog/whats-so-extraordinary-about-the-ordinary" replace />} />
          <Route path="/news/2022/9/29/discovering-montessori" element={<Navigate to="/blog/discovering-montessori" replace />} />
          <Route path="/news/2023/9/26/no-one-is-alone" element={<Navigate to="/blog/no-one-is-alone" replace />} />
          <Route path="/news/2022/12/2/a-case-for-play-based-learning" element={<Navigate to="/blog/a-case-for-play-based-learning" replace />} />
          <Route path="/news/:year/:month/:day/:slug" element={<Navigate to="/blog" replace />} />
          <Route path="/after-school-program" element={<Navigate to="/after-school-activities" replace />} />
          <Route path="/open-house-sign-ups" element={<Navigate to="/open-house-events" replace />} />
          <Route path="/tuition-schedule-1" element={<Navigate to="/tuition-schedule" replace />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </ScrollProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
